import React, { useCallback, useEffect, useState } from 'react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import {
  ErrorGroup,
  ErrorGroupStatus,
  ErrorOccurrence,
  ErrorPlatform,
  FixCandidates,
  FixSignal,
  monitoringApi,
} from '../../api/monitoring';
import { PaginatedResponse } from '../../types';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';

/**
 * The "issues" list for the in-house error monitor — grouped crashes from
 * backend and mobile, with triage (resolve / ignore / reopen). Mirrors the
 * shape of a Sentry issues page: one row per distinct fault, drill in for
 * recent occurrences and stack.
 */

const statusColors: Record<ErrorGroupStatus, 'warning' | 'success' | 'neutral'> = {
  open: 'warning',
  resolved: 'success',
  ignored: 'neutral',
};

const platformColors: Record<ErrorPlatform, 'info' | 'premium'> = {
  backend: 'info',
  mobile: 'premium',
};

const fixLabels: Record<FixSignal['verdict'], string> = {
  likely_fixed: 'Likely fixed',
  quiet: 'Gone quiet',
};

const fixColors: Record<FixSignal['verdict'], 'success' | 'neutral'> = {
  likely_fixed: 'success',
  quiet: 'neutral',
};

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const selectStyle: React.CSSProperties = {
  height: 38,
  padding: '0 32px 0 12px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-700)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  cursor: 'pointer',
};

export const ErrorMonitoring: React.FC = () => {
  usePage('Error Monitoring');
  const toast = useToast();

  const [data, setData] = useState<PaginatedResponse<ErrorGroup> | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ErrorGroupStatus | ''>('open');
  const [platform, setPlatform] = useState<ErrorPlatform | ''>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<{
    group: ErrorGroup;
    occurrences: ErrorOccurrence[];
  } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { can } = useAuth();
  const canManage = can('monitoring.manage');

  /*
   * Open errors that look fixed. Nobody closes an error when they fix it, so
   * the list fills with ones that stopped long ago. The backend reads what
   * it has (how long each has been quiet against how often it used to
   * happen, and whether new code has gone out since) and this panel offers
   * them back to be marked. A wrong call is cheap: a resolved error that
   * happens again reopens itself and emails as a regression.
   */
  const [candidates, setCandidates] = useState<FixCandidates | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [resolving, setResolving] = useState(false);

  const fetchCandidates = useCallback(async () => {
    try {
      const res = await monitoringApi.fixCandidates();
      setCandidates(res);
      // Start with the confident ones ticked. "Gone quiet" claims less, so
      // it is left for somebody to choose.
      setPicked(
        new Set(
          res.items
            .filter((g) => g.fixSignal.verdict === 'likely_fixed')
            .map((g) => g.id),
        ),
      );
    } catch {
      // The panel is a convenience over the list. If it cannot load, the
      // list below still works, so it simply does not show.
      setCandidates(null);
    }
  }, []);

  useEffect(() => {
    void fetchCandidates();
  }, [fetchCandidates]);

  function togglePicked(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function markFixed(ids: string[]) {
    if (!ids.length) return;
    try {
      setResolving(true);
      const resolved = await monitoringApi.resolveMany(ids);
      toast.success(
        resolved === 1 ? 'Marked 1 error as fixed.' : `Marked ${resolved} errors as fixed.`,
      );
      await Promise.all([fetchCandidates(), fetchErrors()]);
    } catch (err) {
      toast.error(err, 'Could not mark those as fixed.');
    } finally {
      setResolving(false);
    }
  }

  const fetchErrors = useCallback(async () => {
    try {
      setLoading(true);
      const res = await monitoringApi.listErrors({
        status: status || undefined,
        platform: platform || undefined,
        page,
        limit: 20,
      });
      setData(res);
    } catch (err) {
      toast.error(err, 'Failed to load errors.');
    } finally {
      setLoading(false);
    }
  }, [status, platform, page, toast]);

  useEffect(() => {
    setPage(1);
  }, [status, platform]);

  useEffect(() => {
    void fetchErrors();
  }, [fetchErrors]);

  async function openDetail(id: string) {
    try {
      setSelected(await monitoringApi.getError(id));
    } catch (err) {
      toast.error(err, 'Failed to load error detail.');
    }
  }

  async function triage(id: string, next: ErrorGroupStatus) {
    try {
      setBusyId(id);
      await monitoringApi.setStatus(id, next);
      toast.success(`Marked ${next}.`);
      setSelected(null);
      void fetchErrors();
      void fetchCandidates();
    } catch (err) {
      toast.error(err, 'Could not update the error.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {candidates?.items.length ? (
        <Card padding={20}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 16,
              flexWrap: 'wrap',
              marginBottom: 16,
            }}
          >
            <div style={{ maxWidth: 680 }}>
              <h3 style={{ margin: 0, fontSize: 15, color: 'var(--neutral-900)' }}>
                {candidates.items.length === 1
                  ? '1 open error looks fixed'
                  : `${candidates.items.length} open errors look fixed`}
              </h3>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--neutral-600)' }}>
                These have been quiet for longer than they ever used to be.
                "Likely fixed" means new code has gone out since and run for at
                least two days without the error. "Gone quiet" means no new
                release has been seen, so the error may only be rare. Anything
                you mark as fixed reopens by itself if it happens again.
              </p>
            </div>
            {canManage ? (
              <Button
                onClick={() => void markFixed(Array.from(picked))}
                loading={resolving}
                disabled={picked.size === 0}
              >
                {picked.size === 0
                  ? 'Mark as fixed'
                  : `Mark ${picked.size} as fixed`}
              </Button>
            ) : null}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                minWidth: 820,
                borderCollapse: 'collapse',
                fontSize: 13,
              }}
            >
              <tbody>
                {candidates.items.map((g) => (
                  <tr key={g.id} style={{ borderTop: '1px solid var(--neutral-200)' }}>
                    {canManage ? (
                      <td style={{ padding: '12px 8px 12px 0', width: 24 }}>
                        <input
                          type="checkbox"
                          checked={picked.has(g.id)}
                          onChange={() => togglePicked(g.id)}
                          aria-label={`Select ${g.type}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: '12px 14px 12px 0', maxWidth: 320 }}>
                      <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>
                        {g.type}
                      </div>
                      <div
                        style={{
                          color: 'var(--neutral-500)',
                          fontSize: 12,
                          fontFamily: 'monospace',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 300,
                        }}
                      >
                        {g.message}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                      <Badge variant={platformColors[g.platform]}>{g.platform}</Badge>{' '}
                      <Badge variant={fixColors[g.fixSignal.verdict]}>
                        {fixLabels[g.fixSignal.verdict]}
                      </Badge>
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--neutral-700)' }}>
                      {g.fixSignal.reason}
                    </td>
                    <td style={{ padding: '12px 0 12px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button variant="ghost" size="sm" onClick={() => void openDetail(g.id)}>
                        View
                      </Button>
                      {canManage ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => void markFixed([g.id])}
                          disabled={resolving}
                        >
                          Mark fixed
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      <Card padding={20}>
        <p
          style={{
            margin: '0 0 16px',
            fontSize: 13,
            color: 'var(--neutral-600)',
            maxWidth: 680,
          }}
        >
          Unhandled server faults and mobile crashes, grouped by fingerprint.
          New errors and regressions email the on-call address immediately;
          ongoing volume is digested every 30 minutes. Resolve an issue to
          silence it — it reopens automatically if it happens again.
        </p>

        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as ErrorGroupStatus | '')}
            style={selectStyle}
          >
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="ignored">Ignored</option>
          </select>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as ErrorPlatform | '')}
            style={selectStyle}
          >
            <option value="">All platforms</option>
            <option value="backend">Backend</option>
            <option value="mobile">Mobile</option>
          </select>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner />
          </div>
        ) : !data?.items.length ? (
          <div
            style={{
              padding: 48,
              textAlign: 'center',
              fontSize: 14,
              color: 'var(--neutral-500)',
            }}
          >
            {status === 'open'
              ? 'No open errors. All clear.'
              : 'No errors match this filter.'}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  minWidth: 820,
                  borderCollapse: 'collapse',
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {['Error', 'Platform', 'Count', 'Last seen', 'Status', ''].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            padding: '12px 14px',
                            textAlign: 'left',
                            fontSize: 12,
                            color: 'var(--neutral-600)',
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                          }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((g) => (
                    <tr key={g.id}>
                      <td style={{ padding: '12px 14px', maxWidth: 420 }}>
                        <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>
                          {g.type}
                        </div>
                        <div
                          style={{
                            color: 'var(--neutral-500)',
                            fontSize: 12,
                            fontFamily: 'monospace',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 400,
                          }}
                        >
                          {g.message}
                        </div>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <Badge variant={platformColors[g.platform]}>{g.platform}</Badge>
                      </td>
                      <td style={{ padding: '12px 14px', fontWeight: 600 }}>{g.count}</td>
                      <td style={{ padding: '12px 14px', color: 'var(--neutral-600)' }}>
                        {formatDateTime(g.lastSeenAt)}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <Badge variant={statusColors[g.status]}>{g.status}</Badge>
                        {g.fixSignal ? (
                          <span title={g.fixSignal.reason} style={{ marginLeft: 6 }}>
                            <Badge variant={fixColors[g.fixSignal.verdict]}>
                              {fixLabels[g.fixSignal.verdict]}
                            </Badge>
                          </span>
                        ) : null}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                        <Button variant="secondary" onClick={() => void openDetail(g.id)}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      {selected ? (
        <ErrorDetail
          fixSignal={
            candidates?.items.find((g) => g.id === selected.group.id)?.fixSignal ?? null
          }
          group={selected.group}
          occurrences={selected.occurrences}
          busy={busyId === selected.group.id}
          onTriage={(next) => void triage(selected.group.id, next)}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
};

const ErrorDetail: React.FC<{
  fixSignal: FixSignal | null;
  group: ErrorGroup;
  occurrences: ErrorOccurrence[];
  busy: boolean;
  onTriage: (status: ErrorGroupStatus) => void;
  onClose: () => void;
}> = ({ fixSignal, group, occurrences, busy, onTriage, onClose }) => {
  const latest = occurrences[0];
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(620px, 100%)',
          height: '100%',
          background: 'var(--neutral-100)',
          overflowY: 'auto',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: 'var(--neutral-900)' }}>
              {group.type}
            </h2>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <Badge variant={platformColors[group.platform]}>{group.platform}</Badge>
              <Badge variant={statusColors[group.status]}>{group.status}</Badge>
              <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                {group.count} occurrence{group.count === 1 ? '' : 's'}
              </span>
            </div>
          </div>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {group.status !== 'resolved' ? (
            <Button onClick={() => onTriage('resolved')} loading={busy}>
              Resolve
            </Button>
          ) : (
            <Button onClick={() => onTriage('open')} loading={busy}>
              Reopen
            </Button>
          )}
          {group.status !== 'ignored' ? (
            <Button variant="secondary" onClick={() => onTriage('ignored')} loading={busy}>
              Ignore
            </Button>
          ) : null}
        </div>

        {fixSignal && group.status === 'open' ? (
          <div
            style={{
              padding: 12,
              borderRadius: 8,
              background: 'var(--neutral-200)',
              fontSize: 13,
              color: 'var(--neutral-800)',
            }}
          >
            <Badge variant={fixColors[fixSignal.verdict]}>{fixLabels[fixSignal.verdict]}</Badge>
            <div style={{ marginTop: 8 }}>{fixSignal.reason}</div>
          </div>
        ) : null}

        <DetailField label="Message" value={group.message} mono />
        {latest?.context ? <DetailField label="Location" value={latest.context} mono /> : null}
        {group.lastRelease ? <DetailField label="Release" value={group.lastRelease} mono /> : null}
        <DetailField
          label="First seen"
          value={new Date(group.firstSeenAt).toLocaleString('en-NG')}
        />

        {latest?.stack ? (
          <div>
            <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginBottom: 6 }}>
              Latest stack
            </div>
            <pre
              style={{
                margin: 0,
                padding: 12,
                background: 'var(--neutral-900)',
                color: '#e5e7eb',
                borderRadius: 8,
                fontSize: 12,
                lineHeight: 1.5,
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {latest.stack}
            </pre>
          </div>
        ) : null}

        {occurrences.length > 1 ? (
          <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
            Showing the latest of {occurrences.length} retained occurrences.
          </div>
        ) : null}
      </div>
    </div>
  );
};

const DetailField: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div>
    <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginBottom: 4 }}>{label}</div>
    <div
      style={{
        fontSize: 13,
        color: 'var(--neutral-900)',
        fontFamily: mono ? 'monospace' : undefined,
        wordBreak: 'break-word',
      }}
    >
      {value}
    </div>
  </div>
);
